import { PeriodType } from '../../../../shared/types/prompt/PeriodType';
import { ChartMatrixData } from '../../../prompt/preview/components/stats/models/StatsModel';
import {
  normalizeValue,
} from '../../../../shared/utils/utils';
import { getFirstSeriesPalette, getSecondSeriesPalette } from '../../../../shared/utils/chartTheme';
import { prepareHeaderDate } from '../../../prompt/preview/components/stats/helpers/StatsHelper';
import {
  AllBrandsStatsResponse,
  ProjectSovPoint,
  ProjectVisibilityPoint,
} from '../../../../shared/models/projects/stats/AllBrandsStatsResponse';
import {
  ProjectDailyStatsRecord,
  ProjectDailyStatsResponse,
} from '../../../../shared/models/projects/stats/ProjectDailyStatsResponse';
import { ProjectGeneral, ProjectGeneralData } from '../models/ProjectGeneralData';
import { ChatType, getChatTypeToName, projectChatType } from '../../../../shared/types/monitor/ChatType';
import { MonitorPromptsKeywordsStatsResponse } from '../../../../shared/services/monitor/keywords/stats/MonitorPromptsKeywordsStatsResponse';
import { FilterItem } from '../../../../shared/components/filter-box/models/FilterBoxModel';
import { PROJECTS } from '../../../../shared/routes/path';
import { MonitorPeriod } from '../../../../shared/types/monitor/MonitorPeriod';
import { AccountResponse } from '../../../../shared/models/account/AccountResponse';
import { PieChartData } from '../../../../shared/models/common/PieChartData';
import { t } from 'i18next';
import { PromptsData } from '../../../prompt/models/PromptsData';
import { MonitorType } from '../../../../shared/types/monitor/MonitorType';
import { getMonitorTypesForChatType } from '../../../../shared/helpers/MonitorTypeHelpers';
import { TagsResponse } from '../../../../shared/models/tags/TagsResponse';
import { KeywordResponse } from '../../../../shared/models/monitor/KeywordResponse';
import { findTagIdInKeyword } from '../../../../shared/helpers/keyword/KeywordHelper';
import { getTagLabel } from '../../../../shared/helpers/tags/TagsHelper';

export const FIRST_CHATBEAT_PROJECT_KEY = `first_chatbeat_project`;

export const prepareProjectGeneralData = (
  projectDailyStatsRecords: ProjectDailyStatsResponse
): ProjectGeneral => {
  if (!projectDailyStatsRecords) {
    return { full: null, withoutLast: null };
  }

  const recordsArray = Object.entries(projectDailyStatsRecords).sort(
    ([dateA], [dateB]) => new Date(dateA).getTime() - new Date(dateB).getTime()
  );

  const fullData = calculateAverages(Object.values(projectDailyStatsRecords));
  const withoutLastData = calculateAverages(recordsArray.slice(0, -1).map(([, record]) => record));

  return { full: fullData, withoutLast: withoutLastData };
};

const calculateAverages = (records: ProjectDailyStatsRecord[]): ProjectGeneralData | null => {
  if (records.length === 0) return null;

  let totalPosition = 0,
    positionCount = 0;

  let totalVisibilityScore = 0,
    visibilityCount = 0;

  const medianPositionsList = records
    .filter((record) => record.medianPosition !== null)
    .map((record) => record.medianPosition);
  records.forEach((record) => {
    if (record.averagePosition > 0) {
      totalPosition += Number(record.averagePosition);
      positionCount++;
    }

    if (record.averageVisibilityScore > 0) {
      totalVisibilityScore += Number(record.averageVisibilityScore);
      visibilityCount++;
    }
  });

  return {
    averagePosition: positionCount > 0 ? Number((totalPosition / positionCount).toFixed(1)) : 0,
    averageVisibilityScore:
      visibilityCount > 0 ? Number((totalVisibilityScore / visibilityCount).toFixed(2)) : 0,
    medianPosition: calculateMedian(medianPositionsList),
  };
};

const calculateMedian = (data: number[]): number => {
  if (data.length === 0) {
    return 0;
  }
  data.sort((a, b) => a - b);

  const mid = Math.floor(data.length / 2);
  if (data.length % 2 === 0) {
    return parseFloat(((Number(data[mid - 1]) + Number(data[mid])) / 2).toFixed(1));
  }
  return data[mid];
};

export const createProjectStatsMatrix = (
  periodType: PeriodType,
  projectDailyStatsRecords: ProjectDailyStatsResponse
): ChartMatrixData => {
  if (!projectDailyStatsRecords) {
    return { matrix: [], secondMatrix: [], colorsMap: new Map<string, string>() };
  }

  const matrixVisibilityScore: (string | number)[][] = [];
  const headersEstimatedTraffic: string[] = ['date', t('brandScore')];
  const headersCombined: string[] = ['date', t('averagePosition')];

  const uniqueDates: Set<string> = new Set(
    Object.keys(projectDailyStatsRecords).map((date) => date.toString())
  );
  const colorsMap = new Map<string, string>([
    [t('brandScore'), getFirstSeriesPalette()[0]],
    [t('averagePosition'), getSecondSeriesPalette()[0]],
  ]);

  const positionScoreMap = new Map<string, number>();

  uniqueDates.forEach((date) => {
    const headerDate = prepareHeaderDate(date, periodType);
    const record = projectDailyStatsRecords[date.toString()];

    const rowVisibilityScore: (string | number)[] = [headerDate];
    rowVisibilityScore.push(record.averageVisibilityScore ?? (null as unknown as string | number));
    matrixVisibilityScore.push(rowVisibilityScore);

    if (record.averagePosition !== undefined) {
      positionScoreMap.set(headerDate, record.averagePosition);
    }
  });

  const combinedMatrix: (string | number)[][] = [];
  combinedMatrix.push(headersCombined);

  uniqueDates.forEach((date) => {
    const headerDate = prepareHeaderDate(date, periodType);

    combinedMatrix.push([
      headerDate,
      positionScoreMap.get(headerDate) ?? (null as unknown as string | number),
    ]);
  });

  matrixVisibilityScore.unshift(headersEstimatedTraffic);

  return {
    matrix: matrixVisibilityScore,
    secondMatrix: combinedMatrix,
    colorsMap,
  };
};

/**
 * Builds the chart matrix for the Visibility % time-series. Mirrors the Brand Score
 * matrix shape from `createProjectStatsMatrix` (header row + `[headerDate, value]` rows)
 * and reuses `prepareHeaderDate` so the bucket labels line up exactly with the
 * average-position series on the same chart. Used by the V2 preview page to plot
 * Visibility instead of Brand Score on `BrandPositionChart`.
 */
export const createVisibilityMatrix = (
  periodType: PeriodType,
  points: ProjectVisibilityPoint[] | null | undefined
): (string | number)[][] => {
  const header: (string | number)[] = ['date', t('visibility')];
  if (!points || points.length === 0) {
    return [header];
  }

  const rows: (string | number)[][] = points.map((point) => [
    prepareHeaderDate(point.date, periodType),
    point.visibility ?? (null as unknown as string | number),
  ]);

  return [header, ...rows];
};

export const createAllModelsStatsMatrix = (
  data: MonitorPromptsKeywordsStatsResponse[],
  firmName: string,
  accountResponse: AccountResponse | null,
  pieChartWithOthers: PieChartData[],
  keywordsMapper: Map<string, string>,
  visibleChatTypes?: string[]
): { matrix: string[][]; selectedKeywords: string[] } => {
  if (!data || data.length === 0) {
    return { matrix: [], selectedKeywords: [] };
  }

  if (!pieChartWithOthers || pieChartWithOthers.length === 0) {
    return { matrix: [], selectedKeywords: [] };
  }

  const keywordsPattern = pieChartWithOthers.map((data) => data.name);
  const keywordOrder = new Map<string, number>(
    keywordsPattern.map((keyword, index) => [keyword, index])
  );

  const groupedByChatType = new Map<string, Map<string, { sum: number; count: number }>>();
  const uniqueMonitorIds = new Set<number>(data.map((item) => item.monitorId));

  data.forEach(({ chatType, keyword, averageShareOfVoice }) => {
    const cat = chatType?.toString() || '';
    if (!groupedByChatType.has(cat)) {
      groupedByChatType.set(cat, new Map());
    }

    const chatMap = groupedByChatType.get(cat)!;
    if (!chatMap.has(keyword)) {
      chatMap.set(keyword, { sum: 0, count: uniqueMonitorIds.size });
    }

    chatMap.get(keyword)!.sum += averageShareOfVoice ?? 0;
  });

  const keywordAverages: { keyword: string; averageShareOfVoice: number }[] = [];

  groupedByChatType.forEach((chatMap) => {
    chatMap.forEach(({ sum, count }, keyword) => {
      keywordAverages.push({ keyword, averageShareOfVoice: sum / count });
    });
  });

  const keywordSumMap = new Map<string, number>();

  keywordAverages.forEach(({ keyword, averageShareOfVoice }) => {
    keywordSumMap.set(keyword, (keywordSumMap.get(keyword) || 0) + averageShareOfVoice);
  });

  const sortedKeywords = Array.from(keywordSumMap.entries())
    .map(([keyword, totalShare]) => ({ keyword, averageShareOfVoice: totalShare }))
    .sort((a, b) => b.averageShareOfVoice - a.averageShareOfVoice);

  const selectedHighKeywords = sortedKeywords.slice(0, 3).map(({ keyword }) => keyword);
  if (!selectedHighKeywords.includes(firmName)) {
    selectedHighKeywords.push(firmName);
  }

  const selectedKeywords = selectedHighKeywords.slice().sort((a, b) => {
    const indexA = keywordOrder.get(a) ?? Infinity;
    const indexB = keywordOrder.get(b) ?? Infinity;
    return indexA - indexB;
  });

  while (selectedKeywords.length < 4) {
    const additionalKeyword = sortedKeywords.find(({ keyword }) => !selectedKeywords.includes(keyword));
    if (additionalKeyword) {
      selectedKeywords.push(additionalKeyword.keyword);
    } else {
      break;
    }
  }

  const matrix: string[][] = [['', ...selectedKeywords]];

  const chatTypes = visibleChatTypes ?? projectChatType(accountResponse, true);
  chatTypes.forEach((chatType) => {
    const chatMap = groupedByChatType.get(chatType) || new Map();
    const row: string[] = [getChatTypeToName(chatType as ChatType) || chatType];

    selectedKeywords.forEach((keyword) => {
      const entry = chatMap.get(keyword);
      if (entry) {
        const avg = normalizeValue(entry.sum / entry.count);
        const rounded = Number.isInteger(avg) ? String(avg) : avg.toFixed(1);
        row.push(rounded);
      } else {
        row.push('0');
      }
    });

    matrix.push(row);
  });
  return { matrix, selectedKeywords };
};

/**
 * Returns up to 4 top keywords for the SoV-over-time chart, mirroring the keyword
 * selection logic of `createAllModelsStatsMatrix`:
 * - Sort all brands by `shareOfVoice` descending
 * - Take the top 3 keywords
 * - Ensure the monitored brand (`firmName`) is included
 * - Cap at 4
 */
export const getTopSovBrands = (
  allBrands: AllBrandsStatsResponse[],
  firmName: string
): string[] => {
  if (!allBrands || allBrands.length === 0) return [];

  const sorted = [...allBrands].sort((a, b) => b.shareOfVoice - a.shareOfVoice);
  const topKeywords = sorted.slice(0, 3).map((b) => b.keyword);

  if (!topKeywords.includes(firmName)) {
    topKeywords.push(firmName);
  }

  return topKeywords.slice(0, 4);
};

/**
 * Pivots flat SoV time-series points into a chart matrix compatible with `LineChart`:
 * - header row: ['date', ...topKeywords.map(resolveLabel)]
 * - one row per distinct date (in the order they appear in `points`, ASC),
 *   each cell being the SoV % for that keyword or 0 when absent.
 *
 * Returns a header-only matrix when there are no points or keywords.
 */
export const createSovOverTimeMatrix = (
  period: PeriodType,
  points: ProjectSovPoint[],
  topKeywords: string[],
  resolveLabel: (keyword: string) => string
): (string | number)[][] => {
  const header: (string | number)[] = ['date', ...topKeywords.map(resolveLabel)];

  if (!points || points.length === 0 || topKeywords.length === 0) {
    return [header];
  }

  // Collect distinct dates preserving ASC order (backend sorts ASC).
  const seenDates: string[] = [];
  const dateSet = new Set<string>();
  for (const point of points) {
    if (!dateSet.has(point.date)) {
      dateSet.add(point.date);
      seenDates.push(point.date);
    }
  }

  // Build a lookup: date → keyword → shareOfVoice
  const lookup = new Map<string, Map<string, number>>();
  for (const point of points) {
    if (!lookup.has(point.date)) {
      lookup.set(point.date, new Map());
    }
    lookup.get(point.date)!.set(point.keyword, point.shareOfVoice);
  }

  const rows: (string | number)[][] = seenDates.map((date) => {
    const byKeyword = lookup.get(date)!;
    return [
      prepareHeaderDate(date, period),
      ...topKeywords.map((k) => byKeyword.get(k) ?? 0),
    ];
  });

  return [header, ...rows];
};

export const getPeriodParam = (monitorPeriod: MonitorPeriod): string => {
  return monitorPeriod ? monitorPeriod.toLowerCase() : 'daily';
};

const getPeriodAndChatParam = (chat: ChatType | null, period: string): string => {
  const chatParam = chat ? chat.toLowerCase() : 'gpt';
  return `${period}/${chatParam}`;
};

export const periodFilter = (
  chat: ChatType | null,
  projectId: number,
  monitorPeriod: MonitorPeriod
): FilterItem[] => {
  return [
    {
      url: `${PROJECTS}/${projectId}/${getPeriodAndChatParam(chat, 'daily')}`,
      label: t('dailyFilter'),
      isActive: monitorPeriod === 'DAILY',
    },
    {
      url: `${PROJECTS}/${projectId}/${getPeriodAndChatParam(chat, 'weekly')}`,
      label: t('weeklyFilter'),
      isActive: monitorPeriod === 'WEEKLY',
    },
    {
      url: `${PROJECTS}/${projectId}/${getPeriodAndChatParam(chat, 'monthly')}`,
      label: t('monthlyFilter'),
      isActive: monitorPeriod === 'MONTHLY',
    },
  ];
};

export const generateVisibilitySummary = (
  firmName: string,
  firmNameLabel: string,
  visibility: string | null,
  promptsData: PromptsData[],
  chatType: ChatType,
  allModelsMatrix: string[][] | null,
  tags: TagsResponse[],
  commonKeywords: KeywordResponse[]
): string[] => {
  if (!visibility) {
    return [];
  }

  const visibilityText = t(visibility);

  if (!promptsData.length) {
    return [
      `<div class="insights-box__title">${t('brandVisibilityText', { firmName: firmNameLabel, visibilityText })}</div>`,
    ];
  }

  let averageShareOfVoice: { model: string; average: number; displayName: string }[] = [];

  if (allModelsMatrix && allModelsMatrix.length > 1) {
    const headerRow = allModelsMatrix[0];
    const brandColIndex = headerRow.findIndex(
      (col) => col.toLowerCase() === firmName.toLowerCase() // raw firmName
    );

    if (brandColIndex !== -1) {
      averageShareOfVoice = allModelsMatrix.slice(1).map((row) => ({
        model: row[0],
        average: parseFloat(row[brandColIndex]) || 0,
        displayName: row[0],
      }));
    }
  }

  const maxSOV = Math.max(...averageShareOfVoice.map((sov) => sov.average));
  const minSOV = Math.min(...averageShareOfVoice.map((sov) => sov.average));

  const highestSOVModels = averageShareOfVoice
    .filter((sov) => sov.average === maxSOV)
    .map((sov) => sov.displayName)
    .join(', ');

  const lowestSOVModels = averageShareOfVoice
    .filter((sov) => sov.average === minSOV)
    .map((sov) => sov.displayName)
    .join(', ');

  const validPrompts =
    promptsData.filter((item) => {
      const position = parseFloat(item.averagePosition ?? '');
      return !isNaN(position);
    }) || [];

  const topRankingQuery = validPrompts.reduce((best, current) => {
    const currentPos = parseFloat(current.averagePosition || '');
    const bestPos = parseFloat(best.averagePosition || '');
    return currentPos < bestPos ? current : best;
  }, validPrompts[0] || {});

  if (maxSOV <= 0 && chatType === 'ALL') {
    return [`<div class="insights-box__content">${t('insightNotVisible')}</div>`];
  }

  const normalizedMaxSOV = normalizeValue(maxSOV);
  const normalizedMinSOV = maxSOV !== minSOV ? normalizeValue(minSOV) : 0;
  const prompt = topRankingQuery.prompt;
  const keyword = topRankingQuery.keyword;
  const tagId = commonKeywords ? findTagIdInKeyword(keyword, commonKeywords) : null;
  const keywordLabel = tags ? getTagLabel(tags, tagId, keyword) : keyword;
  const averagePosition = topRankingQuery.averagePosition;

  return [
    `<div class="insights-box__title">${t('brandVisibilityFull.header', {
      firmName: firmNameLabel,
      visibilityText,
    })}</div>${
      maxSOV > 0
        ? `<div class="insights-box__content">
        ${t('brandVisibilityFull.high', { highestSOVModels, normalizedMaxSOV })}
        ${maxSOV !== minSOV ? t('brandVisibilityFull.low', { lowestSOVModels, normalizedMinSOV }) : ''}
    </div>`
        : ''
    }`,
    `${
      prompt && prompt.trim() !== ''
        ? `<div class="insights-box__title"> ${t('brandVisibilityFull.topQuery', {
            prompt,
          })}</div><div class="insights-box__content">${t('brandVisibilityFull.topQueryKeywords', {
            keyword: keywordLabel,
            averagePosition,
          })}</div>`
        : ''
    }`,
  ];
};
